import { FC, useState, useMemo } from 'react';
import { ButtonGroup, Fade, Grid, MenuItem, Box, TextField, Autocomplete } from '@mui/material';
import { Modal, ModalTitle, Loader, SMBSelect, ItemValue, Button } from '../../../components';
import { useSnackbar } from 'notistack';
import {
  IMissingDocumentTemplate,
  IAvailableTemplateTypes,
  ITemplateVendorType,
  ITemplateSubcontractorType
} from '../../../models';
import { Save } from '@mui/icons-material';
import {
  createContentLibrary,
  createTemplate,
  updateContentLibrary,
  updateTemplate
} from '../../../fetch';
import { unCamelCase } from '../../../helpers';

interface IAssignModal {
  open: boolean;
  onClose: (shouldUpdate?: boolean) => void;
  isLoadingMissingDocs: boolean;
  missingDocs: IMissingDocumentTemplate[];
  isLoadingTypes: boolean;
  types: IAvailableTemplateTypes[];
  refetchContentTypes?: () => void;
  currentOption: any;
  isContentLibrary?: boolean;
  isContractTemplate?: boolean;
}

const SUBCONTRACTOR_ID = 1;
const MATERIAL_SUPPLIER_ACTIVE_ID = 2;

export const AssignModal: FC<IAssignModal> = ({
  open,
  onClose,
  isLoadingMissingDocs,
  missingDocs,
  types,
  isLoadingTypes,
  currentOption,
  isContentLibrary,
  refetchContentTypes,
  isContractTemplate
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPandaDocTemplateId, setSelectedPandaDocTemplateId] = useState<string>('');
  const [selectedSubcontractorTypeId, setSelectedSubcontractorTypeId] = useState<number | string>(
    currentOption?.subcontractorTypeId ?? ''
  );
  const [selectedVendorTypeId, setSelectedVendorTypeId] = useState<string>(
    currentOption?.vendorTypeId ?? ''
  );
  const [selectedTemplateType, setSelectedTemplateType] = useState<string>(
    currentOption?.type ?? currentOption?.contentPlaceHolderBlock ?? ''
  );
  const [selectedSubcontractorValues, setSelectedSubcontractorValues] = useState<any>([]);
  const [inputSelectedSubcontractOptions, setInputSelectedSubcontractorOptions] = useState('');

  const handleClose = (shouldUpdate?: boolean) => {
    setSelectedPandaDocTemplateId('');
    setSelectedVendorTypeId('');
    setSelectedSubcontractorValues([]);
    setSelectedTemplateType('');
    onClose(shouldUpdate);
  };

  const shouldShowSubcontractorOption: null | boolean = useMemo(
    () =>
      (selectedVendorTypeId && Number(selectedVendorTypeId) === SUBCONTRACTOR_ID) ||
      (selectedVendorTypeId &&
        isContentLibrary &&
        Number(selectedVendorTypeId) === MATERIAL_SUPPLIER_ACTIVE_ID &&
        isContentLibrary),
    [selectedVendorTypeId, isContentLibrary]
  );

  const selectedVendorTypes: ITemplateVendorType[] = useMemo(() => {
    const vendorTypes =
      types?.find(template => template.type === selectedTemplateType)?.vendorTypes ?? [];

    const currentVendorType = {
      vendorTypeId: currentOption?.vendorTypeId,
      vendorType: currentOption?.vendorType,
      subcontractorTypes: null
    };

    if (
      currentOption &&
      currentOption?.vendorTypeId !== SUBCONTRACTOR_ID &&
      currentOption?.vendorTypeId !== MATERIAL_SUPPLIER_ACTIVE_ID
    ) {
      return [currentVendorType, ...vendorTypes].filter(
        (value, index, self) => index === self.findIndex(t => t.vendorTypeId === value.vendorTypeId)
      );
    } else {
      return vendorTypes;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateType, types]);

  const selectedSubcontractorTypes: ITemplateSubcontractorType[] = useMemo(() => {
    const selectedTemplate = types?.find(template => template.type === selectedTemplateType);
    const selectedVendorTypes = selectedTemplate?.vendorTypes.find(
      vendorType => vendorType.vendorTypeId === Number(selectedVendorTypeId)
    );

    const transformed = currentOption?.subcontractorTypes?.map(item => ({
      subcontractorTypeId: Number(item.value),
      subcontractorType: item.description
    }));
    setSelectedSubcontractorValues(shouldShowSubcontractorOption ? transformed : []);

    const mergedArray = (transformed || []).concat(selectedVendorTypes?.subcontractorTypes || []);
    return selectedVendorTypes?.subcontractorTypes ? mergedArray : [];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateType, types, selectedVendorTypeId]);

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          handleClose();
        }}
        maxWidth="sm"
      >
        <Fade in={open}>
          <div>
            {isSaving && <Loader type="overlay" position="centered" />}
            <ModalTitle
              title={`Assign ${isContentLibrary ? 'Content Library' : 'Contract Template'}`}
            />
            <Box mt={1}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  {currentOption && (
                    <ItemValue
                      label={isContentLibrary ? 'Library' : 'Template'}
                      value={isContentLibrary ? currentOption?.documentName : currentOption?.name}
                    />
                  )}
                  {!currentOption && (
                    <SMBSelect
                      required
                      label={isContentLibrary ? 'Library' : 'Template'}
                      disabled={isSaving || isLoadingMissingDocs}
                      value={selectedPandaDocTemplateId}
                      onChange={({ target: { value } }) => setSelectedPandaDocTemplateId(value)}
                      handleClear={() => setSelectedPandaDocTemplateId('')}
                    >
                      {missingDocs &&
                        missingDocs.map((pandaDocTemplate, index) => {
                          return (
                            <MenuItem key={`${index}`} value={pandaDocTemplate.id}>
                              {pandaDocTemplate.name}
                            </MenuItem>
                          );
                        })}
                    </SMBSelect>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <SMBSelect
                    required
                    label="Document Type"
                    disabled={isSaving || isLoadingTypes}
                    value={selectedTemplateType}
                    onChange={({ target: { value } }) => setSelectedTemplateType(value)}
                    handleClear={() => setSelectedTemplateType('')}
                  >
                    {types &&
                      types.map((template, index) => {
                        return (
                          <MenuItem key={`${index}`} value={template.type}>
                            {template.typeDisplayName}
                          </MenuItem>
                        );
                      })}
                  </SMBSelect>
                </Grid>
                {!!selectedTemplateType && (
                  <Grid item xs={12}>
                    <SMBSelect
                      disabled={isSaving}
                      required
                      label="Vendor Type"
                      value={selectedVendorTypeId}
                      onChange={({ target: { value } }) => {
                        setSelectedVendorTypeId(value);
                        // clear subcontractorTypeId if the user changes vendorTypeId to something that doesn't have subcontractor types
                        if (
                          value !== SUBCONTRACTOR_ID.toString() &&
                          value !== MATERIAL_SUPPLIER_ACTIVE_ID.toString()
                        ) {
                          setSelectedSubcontractorValues([]);
                          setSelectedSubcontractorTypeId('');
                        }
                      }}
                      handleClear={() => setSelectedVendorTypeId('')}
                    >
                      {selectedVendorTypes.map((vendorType, index) => {
                        return (
                          <MenuItem key={`${index}`} value={vendorType?.vendorTypeId}>
                            {unCamelCase(vendorType?.vendorType)}
                          </MenuItem>
                        );
                      })}
                    </SMBSelect>
                  </Grid>
                )}
                {shouldShowSubcontractorOption && isContentLibrary && (
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      id="availableSubcontractorTypes"
                      value={selectedSubcontractorValues}
                      onChange={(event, newValue: any[]) => {
                        setSelectedSubcontractorValues(newValue);
                      }}
                      inputValue={inputSelectedSubcontractOptions}
                      onInputChange={(event, newInputValue) => {
                        setInputSelectedSubcontractorOptions(newInputValue);
                      }}
                      options={selectedSubcontractorTypes}
                      getOptionLabel={option => option.subcontractorType}
                      filterSelectedOptions
                      renderOption={(props, option: any) => {
                        return (
                          <li {...props} key={`${option.subcontractorTypeId}`}>
                            {option.subcontractorType}
                          </li>
                        );
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          disabled={false}
                          label={false ? 'Loading...' : 'Available Subcontractor Types'}
                          placeholder="Subcontractor Types"
                        />
                      )}
                    />
                  </Grid>
                )}
                {shouldShowSubcontractorOption && isContractTemplate && (
                  <Grid item xs={12}>
                    <SMBSelect
                      disabled={isSaving}
                      label="Subcontractor Type"
                      handleClear={() => setSelectedSubcontractorTypeId('')}
                      value={selectedSubcontractorTypeId}
                      onChange={({ target: { value } }) =>
                        setSelectedSubcontractorTypeId(Number(value))
                      }
                    >
                      {selectedSubcontractorTypes.map((type, index) => {
                        return (
                          <MenuItem key={`${index}`} value={type.subcontractorTypeId}>
                            {type.subcontractorType}
                          </MenuItem>
                        );
                      })}
                    </SMBSelect>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <ButtonGroup>
                    <Button
                      id="save"
                      color="primary"
                      variant="contained"
                      disabled={!selectedVendorTypeId || !selectedTemplateType}
                      startIcon={<Save />}
                      onClick={async () => {
                        try {
                          setIsSaving(true);
                          if (isContentLibrary) {
                            const data = {
                              pandaDocContentLibraryId:
                                currentOption?.pandaDocContentLibraryDocumentId ??
                                selectedPandaDocTemplateId,
                              name:
                                currentOption?.documentName ??
                                missingDocs?.filter(t => t.id === selectedPandaDocTemplateId)?.[0]
                                  ?.name ??
                                '',
                              vendorTypeId: Number(selectedVendorTypeId),
                              contentPlaceHolderBlock: selectedTemplateType,
                              subcontractorTypes: !shouldShowSubcontractorOption
                                ? []
                                : selectedSubcontractorValues?.map(
                                    item => item.subcontractorTypeId
                                  ) ?? []
                            };
                            currentOption
                              ? await updateContentLibrary(data)
                              : await createContentLibrary(data);
                            refetchContentTypes();
                            handleClose(true);
                            enqueueSnackbar('Assigned content library saved!', {
                              variant: 'success'
                            });
                          } else {
                            const data = {
                              pandaDocTemplateId:
                                currentOption?.pandaDocTemplateId ?? selectedPandaDocTemplateId,
                              name:
                                currentOption?.name ??
                                missingDocs?.filter(t => t.id === selectedPandaDocTemplateId)?.[0]
                                  ?.name ??
                                '',
                              vendorTypeId: Number(selectedVendorTypeId),
                              subcontractorTypeId: selectedSubcontractorTypeId,
                              type: selectedTemplateType
                            };
                            currentOption ? await updateTemplate(data) : await createTemplate(data);
                            handleClose(true);
                            enqueueSnackbar('Assigned contract template saved!', {
                              variant: 'success'
                            });
                          }
                        } catch (error) {
                          enqueueSnackbar(
                            error?.Detail ?? `Error assigning document, please try again.`,
                            {
                              variant: 'error'
                            }
                          );
                          console.log(error);
                        } finally {
                          setIsSaving(false);
                        }
                      }}
                    >
                      Save
                    </Button>
                  </ButtonGroup>
                </Grid>
              </Grid>
            </Box>
          </div>
        </Fade>
      </Modal>
    </>
  );
};
